import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { createContext, use } from "react";
import { cn } from "@/lib/utils";
import { Badge } from "../badge";
import type { SidebarItemBadge } from "../navigation/sidebar";

type ChatPanelLayoutState = "none" | "expanded" | "collapsed";
type ChatPanelSidebarState = Exclude<ChatPanelLayoutState, "none">;

const ChatPanelContext = createContext<ChatPanelLayoutState>("none");

const chatPanelVariants = cva(
  [
    "grid",
    "h-full",
    "min-h-0",
    "min-w-0",
    "w-full",
    "overflow-hidden",
    "relative",
    "rounded-(--radius-md)",
    "after:pointer-events-none",
    "after:absolute",
    "after:inset-0",
    "after:z-10",
    "after:rounded-(--radius-md)",
    "after:border",
    "after:border-outline-subtle",
    "after:content-['']",
    "bg-surface",
    "shadow-elevation-4",
    "text-text-default",
    "max-[599px]:grid-cols-1",
  ],
  {
    variants: {
      sidebar: {
        none: "grid-cols-[minmax(0,1fr)]",
        expanded: "grid-cols-[240px_minmax(0,1fr)]",
        collapsed: "grid-cols-[56px_minmax(0,1fr)]",
      },
    },
    defaultVariants: { sidebar: "none" },
  },
);

const chatPanelSidebarVariants = cva(
  [
    "min-h-0",
    "min-w-0",
    "overflow-hidden",
    "relative",
    "after:pointer-events-none",
    "after:absolute",
    "after:inset-y-0",
    "after:right-0",
    "after:border-r",
    "after:border-outline-subtle",
    "after:content-['']",
    "bg-surface",
    "max-[599px]:hidden",
  ],
  {
    variants: {
      sidebar: {
        expanded: "block",
        collapsed: "block",
        none: "hidden",
      },
    },
    defaultVariants: { sidebar: "expanded" },
  },
);

const chatPanelSidebarHeaderVariants = cva([], {
  variants: {
    sidebar: {
      expanded: ["flex", "items-center", "justify-between"],
      collapsed: ["relative", "flex", "w-(--spacing-48)", "justify-center"],
    },
  },
  defaultVariants: { sidebar: "expanded" },
});

const chatPanelSidebarHeaderBadgeVariants = cva([], {
  variants: {
    sidebar: {
      expanded: "shrink-0",
      collapsed: ["absolute", "-right-(--spacing-4)", "-top-(--spacing-4)"],
    },
  },
  defaultVariants: { sidebar: "expanded" },
});

const chatPanelMainStyles = cva([
  "flex",
  "min-w-0",
  "min-h-0",
  "flex-col",
  "bg-surface",
]);
const chatPanelHeaderStyles = cva([
  "flex",
  "shrink-0",
  "items-center",
  "gap-(--spacing-12)",
  "border-b",
  "border-outline-subtle",
  "bg-surface",
  "px-(--spacing-20)",
  "py-(--spacing-16)",
]);
const chatPanelBodyStyles = cva(["min-h-0", "flex-1", "overflow-hidden"]);
const chatPanelFooterStyles = cva([
  "shrink-0",
  "min-w-0",
  "bg-surface",
  "p-(--spacing-16)",
]);

export interface ChatPanelProps
  extends Omit<React.ComponentPropsWithoutRef<"section">, "className">,
    Omit<VariantProps<typeof chatPanelVariants>, "sidebar"> {
  children: React.ReactNode;
  /** Layout of the optional navigation sidebar. Defaults to no sidebar. */
  sidebar?: ChatPanelSidebarState;
}

export interface ChatPanelSidebarProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "className"> {
  children: React.ReactNode;
}

export interface ChatPanelSidebarHeaderProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "className"> {
  children: React.ReactNode;
  /** Optional notification badge positioned for the current sidebar state. */
  badge?: SidebarItemBadge;
  /** Overrides the surrounding ChatPanel sidebar presentation state. */
  sidebar?: ChatPanelSidebarState;
}

export interface ChatPanelMainProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "className"> {
  children: React.ReactNode;
}

export interface ChatPanelHeaderProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "className"> {
  children: React.ReactNode;
}

export interface ChatPanelBodyProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "className"> {
  children: React.ReactNode;
}

export interface ChatPanelFooterProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "className"> {
  children: React.ReactNode;
}

export function ChatPanel({ children, sidebar, ...props }: ChatPanelProps) {
  const resolvedSidebar: ChatPanelLayoutState = sidebar ?? "none";

  return (
    <ChatPanelContext value={resolvedSidebar}>
      <section
        data-slot="chat-panel"
        data-sidebar={resolvedSidebar}
        className={cn(chatPanelVariants({ sidebar: resolvedSidebar }))}
        {...props}
      >
        {children}
      </section>
    </ChatPanelContext>
  );
}

export function ChatPanelSidebar({
  children,
  ...props
}: ChatPanelSidebarProps) {
  const panelSidebar = use(ChatPanelContext);

  return (
    <aside
      data-slot="chat-panel-sidebar"
      className={cn(chatPanelSidebarVariants({ sidebar: panelSidebar }))}
      {...props}
    >
      {children}
    </aside>
  );
}

export function ChatPanelSidebarHeader({
  badge,
  children,
  sidebar: sidebarProp,
  ...props
}: ChatPanelSidebarHeaderProps) {
  const panelSidebar = use(ChatPanelContext);
  const sidebar =
    sidebarProp ?? (panelSidebar === "collapsed" ? "collapsed" : "expanded");

  return (
    <header
      data-slot="chat-panel-sidebar-header"
      data-sidebar={sidebar}
      className={cn(chatPanelSidebarHeaderVariants({ sidebar }))}
      {...props}
    >
      {children}
      {badge && (
        <span
          className={cn(chatPanelSidebarHeaderBadgeVariants({ sidebar }))}
          data-slot="chat-panel-sidebar-badge"
        >
          <Badge
            count={badge.count}
            tone={badge.tone}
            aria-label={badge.label}
          />
        </span>
      )}
    </header>
  );
}

export function ChatPanelMain({ children, ...props }: ChatPanelMainProps) {
  return (
    <div
      data-slot="chat-panel-main"
      className={cn(chatPanelMainStyles())}
      {...props}
    >
      {children}
    </div>
  );
}

export function ChatPanelHeader({ children, ...props }: ChatPanelHeaderProps) {
  return (
    <header
      data-slot="chat-panel-header"
      className={cn(chatPanelHeaderStyles())}
      {...props}
    >
      {children}
    </header>
  );
}

export function ChatPanelBody({ children, ...props }: ChatPanelBodyProps) {
  return (
    <div
      data-slot="chat-panel-body"
      className={cn(chatPanelBodyStyles())}
      {...props}
    >
      {children}
    </div>
  );
}

export function ChatPanelFooter({ children, ...props }: ChatPanelFooterProps) {
  return (
    <footer
      data-slot="chat-panel-footer"
      className={cn(chatPanelFooterStyles())}
      {...props}
    >
      {children}
    </footer>
  );
}

export type { ChatPanelSidebarState };
